// One data hook for the V1 and V2 home screens (2026-10-04): a single poll of the endpoints the desk needs, instead of eight
// separate timers. Polls every `pollMs` while the tab is visible and stops while it is hidden (best practices 2.3: the
// shared connection ceiling). A failed call keeps the last good value for that slice, so one slow endpoint never blanks
// the desk; `errors` names the slices that failed on the last poll so the screen can say so.
import { useState, useEffect, useCallback, useRef } from 'react'
import { api, schwab, streaming, alerts as alertsApi } from './api'
import { useAccount } from '../context/AccountContext'

export default function useDeskData({ pollMs = 30_000 } = {}) {
  const { account, selectedAccount } = useAccount()
  const [period, setPeriod] = useState('1M')
  const [data, setData] = useState({ health: null, schwab: null, ws: null, alerts: [], main: null, tier2: null, equity: [], signals: [], orders: [] })
  const [errors, setErrors] = useState([])
  const [asOf, setAsOf] = useState(null)
  const alive = useRef(true)

  const load = useCallback(async () => {
    const slices = ['health', 'schwab', 'ws', 'alerts', 'main', 'tier2', 'equity', 'signals', 'orders']
    const res = await Promise.allSettled([
      api.health(), schwab.status(), streaming.status(), alertsApi.active(), api.mainPositions(), api.tier2Positions(),
      api.portfolioHistory(period, selectedAccount), api.signalsRecent(), api.trades(40, selectedAccount),
    ])
    if (!alive.current) return
    const failed = []
    setData(prev => {
      const next = { ...prev }
      const pick = (i, fn) => {
        const r = res[i]
        if (r.status === 'fulfilled' && r.value && r.value.error === undefined) fn(r.value)
        else failed.push(slices[i])
      }
      pick(0, v => { next.health = v })
      pick(1, v => { next.schwab = v })
      pick(2, v => { next.ws = v })
      pick(3, v => { next.alerts = v.alerts || [] })
      pick(4, v => { if (v.success) next.main = v; else failed.push('main') })
      pick(5, v => { next.tier2 = v })
      pick(6, v => { next.equity = (v.data || []).filter(d => d.equity != null && d.equity > 0) })
      pick(7, v => { next.signals = v.signals || [] })
      pick(8, v => { next.orders = v.orders || [] })
      return next
    })
    setErrors(failed)
    setAsOf(new Date().toISOString())
  }, [period, selectedAccount])

  useEffect(() => {
    alive.current = true
    load()
    const id = setInterval(() => { if (document.visibilityState === 'visible') load() }, pollMs)
    return () => { alive.current = false; clearInterval(id) }
  }, [load, pollMs])

  // ACK every unresolved row of one alert type; optimistic, reconciled on failure (same behaviour as the Dashboard).
  const ackGroup = useCallback(async (ids) => {
    const gone = new Set(ids)
    setData(prev => ({ ...prev, alerts: prev.alerts.filter(a => !gone.has(a.id)) }))
    const r = await Promise.allSettled(ids.map(id => alertsApi.resolve(id)))
    if (r.some(x => x.status === 'rejected')) load()
  }, [load])

  return { ...data, account, selectedAccount, period, setPeriod, errors, asOf, reload: load, ackGroup }
}
